import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchJournalEntries } from '../lib/journal';
import AppBrand from '../components/AppBrand';
import { trackEvent } from '../lib/analytics';
import { trackProductEvent } from '../lib/productEvents';

function PerformancePage({ session, theme = 'dark', setTheme }) {
  const navigate = useNavigate();
  const darkMode = theme === 'dark';

  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    trackEvent('performance_opened');

    void trackProductEvent({
      userId: session.user.id,
      eventName: 'performance_opened',
    });
  }, [session.user.id]);

  useEffect(() => {
    let mounted = true;

    async function loadPerformance() {
      try {
        setLoading(true);
        setError('');

        const rows = await fetchJournalEntries(session.user.id);

        if (!mounted) return;
        setEntries(rows || []);
      } catch (err) {
        console.error(err);
        if (mounted) {
          setError(err.message || 'Failed to load performance metrics.');
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    }

    loadPerformance();

    return () => {
      mounted = false;
    };
  }, [session.user.id]);

  const metrics = useMemo(() => {
    const totalTracked = entries.length;

    const wins = entries.filter((entry) => entry.outcome_status === 'win').length;
    const losses = entries.filter((entry) => entry.outcome_status === 'loss').length;
    const breakevens = entries.filter((entry) => entry.outcome_status === 'breakeven').length;
    const skipped = entries.filter((entry) => entry.outcome_status === 'skipped').length;
    const open = entries.filter(
      (entry) => !entry.outcome_status || entry.outcome_status === 'open'
    ).length;

    const resolvedTrades = wins + losses + breakevens;
    const actionableTrades = wins + losses;

    const followedYes = entries.filter((entry) => entry.followed_plan === 'yes').length;
    const followedNo = entries.filter((entry) => entry.followed_plan === 'no').length;
    const followedAnswered = followedYes + followedNo;

    const winRate =
      actionableTrades > 0 ? ((wins / actionableTrades) * 100).toFixed(1) : '0.0';

    const followRate =
      followedAnswered > 0
        ? ((followedYes / followedAnswered) * 100).toFixed(1)
        : '0.0';

    return {
      totalTracked,
      wins,
      losses,
      breakevens,
      skipped,
      open,
      resolvedTrades,
      actionableTrades,
      followedYes,
      followedNo,
      followedAnswered,
      winRate,
      followRate,
    };
  }, [entries]);

  const recentEntries = useMemo(() => {
    return [...entries].slice(0, 8);
  }, [entries]);

  const handleThemeToggle = () => {
    if (!setTheme) return;
    setTheme((prev) => (prev === 'dark' ? 'light' : 'dark'));
  };

  const getOutcomePillClass = (outcome) => {
    switch (outcome) {
      case 'win':
        return 'performance-pill win';
      case 'loss':
        return 'performance-pill loss';
      case 'breakeven':
        return 'performance-pill breakeven';
      case 'skipped':
        return 'performance-pill skipped';
      default:
        return 'performance-pill open';
    }
  };

  const formatOutcomeLabel = (outcome) => {
    if (!outcome) return 'Open';
    if (outcome === 'breakeven') return 'Breakeven';
    return outcome.charAt(0).toUpperCase() + outcome.slice(1);
  };

  if (loading) {
    return (
      <div className={`performance-page-shell ${darkMode ? 'dark' : 'light'}`}>
        <div className="performance-page-card">Loading performance...</div>
      </div>
    );
  }

  return (
    <div className={`performance-page-shell ${darkMode ? 'dark' : 'light'}`}>
      <div className="performance-header">
         <AppBrand theme={theme} />
        <div className="performance-header-copy">
          <h1 className="performance-page-title">Performance Metrics</h1>
          <p className="performance-page-subtitle">
            Review outcomes, process discipline, and recent trade performance.
          </p>
        </div>

        <div className="performance-header-actions">
          <button
            type="button"
            className="desktop-theme-btn"
            onClick={handleThemeToggle}
            aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {darkMode ? '☀' : '🌙'}
          </button>

          <button
            type="button"
            className="desktop-secondary-btn"
            onClick={() => navigate('/')}
          >
            Analyzer
          </button>

          <button
            type="button"
            className="desktop-secondary-btn"
            onClick={() => navigate('/journal')}
          >
            Journal
          </button>
        </div>
      </div>

      {error ? <div className="journal-error-banner">{error}</div> : null}

      <div className="performance-metrics-grid">
        <div className="performance-metric-card emphasis">
          <div className="performance-metric-label">Win Rate</div>
          <div className="performance-metric-value">{metrics.winRate}%</div>
          <div className="performance-metric-subtext">
            Based on wins vs losses only
          </div>
        </div>

        <div className="performance-metric-card emphasis">
          <div className="performance-metric-label">Followed Plan</div>
          <div className="performance-metric-value">{metrics.followRate}%</div>
          <div className="performance-metric-subtext">
            Based on answered plan reviews
          </div>
        </div>

        <div className="performance-metric-card">
          <div className="performance-metric-label">Wins</div>
          <div className="performance-metric-value">{metrics.wins}</div>
        </div>

        <div className="performance-metric-card">
          <div className="performance-metric-label">Losses</div>
          <div className="performance-metric-value">{metrics.losses}</div>
        </div>

        <div className="performance-metric-card">
          <div className="performance-metric-label">Open</div>
          <div className="performance-metric-value">{metrics.open}</div>
        </div>
      </div>

    <div className="performance-page-card">
        <div className="performance-section-header">
          <h2>Recent Trade Journal Results</h2>
        </div>

        {recentEntries.length === 0 ? (
          <p className="performance-empty-copy">
            No journal entries yet. Add trade snapshots to your journal to start
            tracking performance.
          </p>
        ) : (
          <div className="performance-table-wrap">
            <table className="performance-table">
              <thead>
                <tr>
                  <th>Ticker</th>
                  <th>Title</th>
                  <th>Outcome</th>
                  <th>Followed Plan</th>
                  <th>Status</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {recentEntries.map((entry) => (
                  <tr key={entry.id}>
                    <td>{entry.ticker || '—'}</td>
                    <td>{entry.title || 'Untitled'}</td>
                    <td>
                      <span className={getOutcomePillClass(entry.outcome_status)}>
                        {formatOutcomeLabel(entry.outcome_status)}
                      </span>
                    </td>
                    <td>
                      {entry.followed_plan === 'yes'
                        ? 'Yes'
                        : entry.followed_plan === 'no'
                        ? 'No'
                        : 'Not set'}
                    </td>
                    <td>{entry.execution_status || '—'}</td>
                    <td>{new Date(entry.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="performance-content-grid">
        <div className="performance-page-card">
          <div className="performance-section-header">
            <h2>Outcome Breakdown</h2>
          </div>

          <div className="performance-breakdown-list">
            <div className="performance-breakdown-row">
              <span>Wins</span>
              <strong>{metrics.wins}</strong>
            </div>
            <div className="performance-breakdown-row">
              <span>Losses</span>
              <strong>{metrics.losses}</strong>
            </div>
            <div className="performance-breakdown-row">
              <span>Skipped</span>
              <strong>{metrics.skipped}</strong>
            </div>
            <div className="performance-breakdown-row">
              <span>Open</span>
              <strong>{metrics.open}</strong>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PerformancePage;